import { useEffect, useState, useCallback } from 'react';

const THEME_BGS = {
  'sky-linen':      '#e6eef6',
  'sage-clay':      '#ecede4',
  'slate-ice':      '#eef0f3',
  'pearl':          '#f2f0ee',
  'lilac-dust':     '#ece6f3',
  'forest':         '#1a2220',
  'citrus-cream':   '#fff4c8',
  'coral-sunshine': '#ffeae0',
};

/**
 * Mirror the v2 theme onto the document so the AppShell/header/body
 * pick up the matching palette. Listens for `wv-v2-theme-change` events
 * dispatched by the in-header dark/light toggle.
 *
 * Returns `[theme, pickTheme]`. `pickTheme` should be called when the
 * user explicitly chooses a light theme from the picker — it persists
 * the choice as the "last light" so the dark-mode toggle can return.
 */
export function useScheduleTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('v2Theme') || 'sky-linen');

  useEffect(() => {
    localStorage.setItem('v2Theme', theme);
    document.body.classList.add('wv-v2-active');
    document.body.dataset.v2Theme = theme;
    document.documentElement.dataset.v2Theme = theme;
    document.documentElement.classList.add('wv-v2-active');

    const bg = THEME_BGS[theme] || THEME_BGS['sky-linen'];
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    const prevBodyBg = document.body.style.backgroundColor;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;

    return () => {
      document.body.classList.remove('wv-v2-active');
      delete document.body.dataset.v2Theme;
      document.documentElement.classList.remove('wv-v2-active');
      delete document.documentElement.dataset.v2Theme;
      document.documentElement.style.backgroundColor = prevHtmlBg;
      document.body.style.backgroundColor = prevBodyBg;
    };
  }, [theme]);

  useEffect(() => {
    const onChange = () => {
      const next = localStorage.getItem('v2Theme');
      if (next) setTheme(next);
    };
    window.addEventListener('wv-v2-theme-change', onChange);
    return () => window.removeEventListener('wv-v2-theme-change', onChange);
  }, []);

  const pickTheme = useCallback((next) => {
    setTheme(next);
    localStorage.setItem('v2LastLightTheme', next);
  }, []);

  return [theme, pickTheme];
}
